// Copyright (C) 2012-present, The Authors. This program is      

import { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { handleZidMetadataUpdate, optimisticZidMetadataUpdateOnTyping } from '../../actions'
import ComponentHelpers from '../../util/component-helpers'
import NoPermission from './no-permission'
import { Heading, Box, Text } from 'theme-ui'
import emoji from 'react-easy-emoji'
import { CheckboxField } from './CheckboxField'
import ModerateCommentsSeed from './seed-comment'
import Spinner from '../framework/spinner'
import { s } from '../../strings/strings'
import DanskFooter from '../shared/DanskFooter'

const ConversationConfig = () => {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user)
  const { zid_metadata, loading, error } = useSelector((state) => state.zid_metadata)

  const topicRef = useRef(null)
  const descriptionRef = useRef(null)

  const handleStringValueChange = (field) => {
    return () => {
      let val = field === 'topic' ? topicRef.current.value : descriptionRef.current.value
      if (field === 'help_bgcolor' || field === 'help_color') {
        if (!val.length) {
          val = 'default'
        }
      }
      dispatch(handleZidMetadataUpdate(zid_metadata, field, val))
    }
  }

  const handleConfigInputTyping = (field) => {
    return (e) => {
      dispatch(optimisticZidMetadataUpdateOnTyping(zid_metadata, field, e.target.value))
    }
  }

  if (loading) {
    return <Spinner />
  }
  if (ComponentHelpers.shouldShowPermissionsError({ user, zid_metadata, loading })) {
    return <NoPermission />
  }

  return (
    <Box>
      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4]
        }}>
        Konfigurér samtale
      </Heading>
      
      <Text sx={{ display: 'block', mb: [2] }}>
        Her ”konfigurerer” du samtalen – giver den en overskrift, beskriver indhold, ramme og tone og tilpasser den side, som deltagerne skal se.
      </Text>

      <Box sx={{ mb: [4] }}>
        {loading ? <Text>{emoji('💾')} Gemmer</Text> : <Text>{emoji('⚡')} Dit indhold er gemt</Text>}
        {error ? <Text>Fejl ved gemning af indhold. Tjek din forbindelse.</Text> : null}
      </Box>

      <Box sx={{ mb: [5] }}>
        <CheckboxField field="is_active" label="Conversation Is Open">
          Åbn samtale. Når dette felt er slået til, kan brugere kommentere og stemme. slås dette felt fra, vil deltagere ikke kunne deltage. Du kan åbne og lukke for deltagelse i samtalen efter behov.
        </CheckboxField>
      </Box>

      <Box sx={{ mb: [3] }}>
        <Box sx={{ mb: [2] }}>
          <Heading 
            as="h3" 
            sx={{ 
              fontSize: [2, null, 3], 
              lineHeight: 'body', 
              fontWeight: 600,
              display: 'inline'
            }}
          >
            Emne
          </Heading>
          <Text sx={{ lineHeight: 'body', ml: 1 }}>
            - en kort overskrift til din samtale. Fx "Grønne områder i København"
          </Text>
        </Box>
        <input
          ref={topicRef}
          sx={{
            display: 'block',
            fontFamily: 'body',
            fontSize: [2],
            width: '35em',
            borderRadius: 2,
            padding: [2],
            border: '1px solid',
            borderColor: 'mediumGray'
          }}
          data-testid="topic"
          onBlur={handleStringValueChange('topic')}
          onChange={handleConfigInputTyping('topic')}
          defaultValue={zid_metadata.topic}
        />
      </Box>

      <Box sx={{ mb: [3], mt: [5] }}>
        <Box sx={{ mb: [2] }}>
          <Heading 
            as="h3" 
            sx={{ 
              fontSize: [2, null, 3], 
              lineHeight: 'body', 
              fontWeight: 600,
              display: 'inline'
            }}
          >
            Samtalens rammer
          </Heading>
          <Text sx={{ lineHeight: 'body', ml: 1 }}>
            - Skriv om du efterlyser deltagernes tanker om sagen, deres
              konkrete ideer eller noget tredje.
              Skriv hvornår samtalen slutter. Opfordr deltagerne at gå
              ind flere gange og skrive indlæg og stemme om andre
              deltageres. Tilføj evt. hvordan du kan kontaktes.
          </Text>
        </Box>
        <textarea
          ref={descriptionRef}
          sx={{
            display: 'block',
            fontFamily: 'body',
            fontSize: [2],
            width: '35em',
            height: '7em',
            resize: 'none',
            padding: [2],
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'mediumGray'
          }}
          data-testid="description"
          onBlur={handleStringValueChange('description')}
          onChange={handleConfigInputTyping('description')}
          defaultValue={zid_metadata.description}
        />
      </Box>

      <Box sx={{ mb: [3], mt: [5] }}>
        <Box sx={{ mb: [2] }}>
          <Heading 
            as="h3" 
            sx={{ 
              fontSize: [2, null, 3], 
              lineHeight: 'body', 
              fontWeight: 600,
              display: 'inline'
            }}
          >
            Skyd afstemningen i gang!
          </Heading>
        </Box>
        <ModerateCommentsSeed params={{ conversation_id: zid_metadata.conversation_id }} />
      </Box>

      <Heading
        as="h6"
        sx={{
          fontSize: [1, null, 2],
          lineHeight: 'body',
          my: [3, null, 4]
        }}>
        {s.customize_user_interface || 'Tilpas brugergrænsefladen'}
      </Heading>

      <CheckboxField field="importance_enabled" label="Importance Enabled">
        {s.importance_enabled || 'Deltagere kan se "Denne kommentar er vigtig" afkrydsningsfeltet. En deltagers “vigtige” kommentar bliver vægtet lidt højere i stemmeafgivningen.'}
      </CheckboxField>

      <CheckboxField field="vis_type" label="Visualization" isIntegerBool>
        {s.visualization || 'Vis et kort over, hvor drøftelsen står lige nu.'}
      </CheckboxField>

      <CheckboxField field="write_type" label="Comment form" isIntegerBool>
        {s.comment_form || 'Deltagere kan bidrage med egne indlæg.'}
      </CheckboxField>

      {/* <CheckboxField field="help_type" label="Help text" isIntegerBool>
        {s.help_text || 'Vis forklarende tekst over afstemning og visualisering'}
      </CheckboxField> */}

      {/* <CheckboxField
        field="subscribe_type"
        label="Prompt participants to subscribe to updates"
        isIntegerBool>
        {s.subscribe_prompt || 'Opfordre deltagere til at abonnere på opdateringer. En opfordring vises til brugere, når de er færdige med at stemme på alle tilgængelige kommentarer. Hvis aktiveret, kan deltagere valgfrit angive deres e-mailadresse for at modtage notifikationer, når der er nye kommentarer at stemme på.'}
      </CheckboxField> */}

      <Heading
        as="h6"
        sx={{
          fontSize: [1, null, 2],
          lineHeight: 'body',
          my: [3, null, 4]
        }}>
        {s.schemes || 'Indstil deltagelse'}
      </Heading>

      <CheckboxField field="strict_moderation">
        {s.strict_moderation || 'Moderator skal godkende deltageres indlæg før de vises og indgår i debatten (se menupunktet "Moderér")'}
      </CheckboxField>

      <DanskFooter />
    </Box>
  )
}

export default ConversationConfig

// checked={this.props.zid_metadata.is_data_open}
// Comments, votes, and group data can be exported by any user

/* <InputField
            ref={"style_btn"}

            style={{ width: 360 }}
            onBlur={this.handleStringValueChange("style_btn").bind(this)}
            hintText="ie., #e63082"
            onChange={this.handleConfigInputTyping("style_btn")}
            value={this.props.zid_metadata.style_btn}
            floatingLabelText={
              "Customize submit button color" + (canCustomizeColors ? "" : lockedIcon)
            }
            multiLine={true}
          /> */

/* <InputField
            ref={"help_bgcolor"}

            style={{ width: 360 }}
            onBlur={this.handleStringValueChange("help_bgcolor").bind(this)}
            onChange={this.handleConfigInputTyping("help_bgcolor")}
            value={this.props.zid_metadata.help_bgcolor}
            hintText="ie., #e63082"
            floatingLabelText={
              "Customize help text background" + (canCustomizeColors ? "" : lockedIcon)
            }
            multiLine={true}
          /> */

/* <InputField
            ref={"help_color"}

            style={{ width: 360 }}
            onBlur={this.handleStringValueChange("help_color").bind(this)}
            onChange={this.handleConfigInputTyping("help_color")}
            value={this.props.zid_metadata.help_color}
            hintText="ie., #e63082"
            floatingLabelText={"Customize help text color" + (canCustomizeColors ? "" : lockedIcon)}
            multiLine={true}
          /> */

/* <Checkbox
            label="Social sharing buttons"
            ref={"socialbtn_type"}
            checked={this.props.zid_metadata.socialbtn_type === 1 ? true : false}
            onCheck={this.handleIntegerBoolValueChange("socialbtn_type").bind(this)}


          /> */
